import React, { useEffect, useRef } from 'react';
import { MODE_FRAMES, resolvePreset } from 'thinking-orbs/engine';

const MOTION_SPEED = { solving: 0.45, connecting: 0.6 };

const blendMarks = (from, to, amount, fields) => Array.from({ length: Math.max(from.length, to.length) }, (_, index) => {
  const a = from[index] || from[index % from.length] || to[index];
  const b = to[index] || to[index % to.length] || from[index];
  const mark = Object.fromEntries(fields.map((field) => [field, a[field] + (b[field] - a[field]) * amount]));
  mark.a = (from[index] ? a.a ?? 1 : 0) * (1 - amount) + (to[index] ? b.a ?? 1 : 0) * amount;
  return mark;
});

export const blendEntityFrames = (from, to, amount) => {
  if (amount <= 0) return from;
  if (amount >= 1) return to;
  return {
    dots: blendMarks(from.dots, to.dots, amount, ['x', 'y', 'z', 'r', 'white']).sort((a, b) => a.z - b.z),
    lines: blendMarks(from.lines, to.lines, amount, ['x1', 'y1', 'x2', 'y2', 'white', 'w']),
  };
};

export default function EntityOrb({ state, paused, reducedMotion }) {
  const canvasRef = useRef(null);
  const clock = useRef({ elapsed: 0, previous: null });
  const transition = useRef({ from: state, to: state, started: 0 });

  useEffect(() => {
    transition.current = { from: transition.current.to, to: state, started: clock.current.elapsed };
  }, [state]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!context) return;
    let frameId;
    let size = 0;
    let ratio = 1;
    let ink;
    clock.current.previous = null;

    const paint = () => {
      if (!size) return;
      const time = reducedMotion ? 0.6 : clock.current.elapsed / 1000;
      const geometry = (mode) => {
        const preset = resolvePreset(mode, 64);
        return MODE_FRAMES[preset.mode](size, time * preset.speed * (MOTION_SPEED[mode] ?? 0.75), preset.opts);
      };
      const target = geometry(transition.current.to);
      const progress = reducedMotion ? 1 : Math.min(1, (clock.current.elapsed - transition.current.started) / 1000);
      const frame = progress >= 1 || transition.current.from === transition.current.to ? target
        : blendEntityFrames(geometry(transition.current.from), target, progress * progress * (3 - 2 * progress));
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.clearRect(0, 0, size, size);
      context.fillStyle = context.strokeStyle = ink;
      const opacity = (mark) => Math.min(1, Math.max(0, (1 - mark.white) * (mark.a ?? 1)));
      for (const line of frame.lines) {
        context.globalAlpha = opacity(line);
        context.lineWidth = line.w;
        context.beginPath();
        context.moveTo(line.x1, line.y1);
        context.lineTo(line.x2, line.y2);
        context.stroke();
      }
      for (const dot of frame.dots) {
        context.globalAlpha = opacity(dot);
        context.beginPath();
        context.arc(dot.x, dot.y, dot.r, 0, Math.PI * 2);
        context.fill();
      }
      context.globalAlpha = 1;
    };
    const resize = () => {
      size = Math.min(canvas.clientWidth, canvas.clientHeight);
      ratio = window.devicePixelRatio || 1;
      const pixels = Math.round(size * ratio);
      if (canvas.width !== pixels || canvas.height !== pixels) canvas.width = canvas.height = pixels;
      ink = getComputedStyle(canvas).color;
      if (paused || reducedMotion) paint();
    };
    const animate = (now) => {
      if (clock.current.previous !== null) clock.current.elapsed += now - clock.current.previous;
      clock.current.previous = now;
      if (ratio !== (window.devicePixelRatio || 1)) resize();
      paint();
      frameId = window.requestAnimationFrame(animate);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();
    if (!paused && !reducedMotion) frameId = window.requestAnimationFrame(animate);
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frameId);
    };
  }, [paused, reducedMotion]);

  return <canvas ref={canvasRef} className="agent-presence__orb" style={{ width: '100%', height: '100%' }} aria-hidden="true" />;
}
